import { useEffect } from "react";

type UseUploadNavigationBlockProps = {
  isUploading: boolean;
  uploadingCount: number;
};

/**
 * custom hook to prevent navigation away from the page when files are uploading
 * blocks browser navigation (refresh, close tab, back button)
 */
export const useUploadNavigationBlock = ({
  isUploading,
  uploadingCount,
}: UseUploadNavigationBlockProps) => {
  // block browser navigation (refresh, close tab, etc)
  useEffect(() => {
    if (!isUploading) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // modern browsers ignore custom messages, but we still need to return a value
      const message = `${uploadingCount} file${uploadingCount !== 1 ? "s are" : " is"} still uploading. Leaving now will cancel the upload${uploadingCount !== 1 ? "s" : ""}.`;
      e.returnValue = message;
      return message;
    };

    // handle browser back/forward navigation
    const handlePopState = () => {
      const confirmed = window.confirm(
        `${uploadingCount} file${uploadingCount !== 1 ? "s are" : " is"} still uploading. Leaving now will cancel the upload${uploadingCount !== 1 ? "s" : ""}.\n\nAre you sure you want to leave?`,
      );

      if (!confirmed) {
        // push current state again to prevent navigation
        window.history.pushState(null, "", window.location.href);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    // push initial state to enable popstate interception
    window.history.pushState(null, "", window.location.href);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isUploading, uploadingCount]);
};
